import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, X, Mail, Copy, RefreshCw, Filter } from "lucide-react";
import { api, formatApiError } from "../context/AuthContext";

const relativeTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "há instantes";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  const dd = Math.floor(diff / 86400);
  if (dd < 30) return `há ${dd} d`;
  return d.toLocaleDateString("pt-PT");
};

const OfferModal = ({ open, onClose, sessionId, guestEmail }) => {
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    setDraft(null);
    setLoading(true);
    api
      .post(`/admin/concierge/sessions/${sessionId}/generate-offer`)
      .then(({ data }) => setDraft(data))
      .catch((err) =>
        toast.error(formatApiError(err.response?.data?.detail) || "Falha ao gerar oferta")
      )
      .finally(() => setLoading(false));
  }, [open, sessionId]);

  const regenerate = () => {
    setLoading(true);
    api
      .post(`/admin/concierge/sessions/${sessionId}/generate-offer`)
      .then(({ data }) => setDraft(data))
      .catch((err) =>
        toast.error(formatApiError(err.response?.data?.detail) || "Falha ao gerar oferta")
      )
      .finally(() => setLoading(false));
  };

  const copy = () => {
    if (!draft) return;
    const text = `Subject: ${draft.subject}\n\n${draft.body}`;
    navigator.clipboard.writeText(text);
    toast.success("Copiado para a área de transferência");
  };

  const openMailto = () => {
    if (!draft) return;
    const to = guestEmail || "";
    const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(
      draft.subject
    )}&body=${encodeURIComponent(draft.body)}`;
    window.open(url, "_blank");
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-[#0A1128]/60 flex items-center justify-center p-4" data-testid="offer-modal">
      <div className="bg-[#FCFAF8] max-w-2xl w-full max-h-[90vh] flex flex-col border border-[#C5A059]/40 shadow-2xl">
        <div className="bg-[#0A1128] text-[#F4F1EB] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles size={16} className="text-[#C5A059]" strokeWidth={1.4} />
            <div>
              <div className="overline text-[#C5A059] text-[10px]">Rascunho de oferta · Claude AI</div>
              <div className="font-serif-display text-lg font-light">Follow-up personalizado</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#F4F1EB]/70 hover:text-[#F4F1EB] p-1"
            data-testid="offer-close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && (
            <div className="text-center py-10 text-[#3A435E] text-sm">
              <div className="inline-flex gap-1 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-bounce" style={{ animationDelay: "300ms" }} />
                <span className="ml-2">Claude está a escrever…</span>
              </div>
            </div>
          )}
          {draft && (
            <>
              <div>
                <div className="overline text-[#C5A059] text-[10px] mb-1">Assunto</div>
                <div className="font-serif-display text-xl text-[#0A1128]" data-testid="offer-subject">
                  {draft.subject}
                </div>
              </div>
              <div>
                <div className="overline text-[#C5A059] text-[10px] mb-1">Corpo</div>
                <div
                  className="text-sm text-[#0A1128] leading-relaxed whitespace-pre-wrap font-light bg-[#F4F1EB] border border-[#0A1128]/10 p-4"
                  data-testid="offer-body"
                >
                  {draft.body}
                </div>
              </div>
              {draft.guest_name && (
                <div className="text-xs text-[#3A435E]">
                  Destinatário sugerido: <span className="text-[#0A1128] font-medium">{draft.guest_name}</span>
                  {guestEmail && <> · {guestEmail}</>}
                </div>
              )}
            </>
          )}
        </div>
        {draft && (
          <div className="border-t border-[#0A1128]/10 px-6 py-4 flex flex-wrap justify-end gap-3 bg-[#FCFAF8]">
            <button
              onClick={regenerate}
              className="inline-flex items-center gap-2 border border-[#0A1128]/20 text-[#0A1128] hover:border-[#C5A059] hover:text-[#C5A059] px-4 py-2.5 text-xs tracking-[0.2em] uppercase transition-colors"
              data-testid="offer-regenerate"
            >
              <RefreshCw size={13} strokeWidth={1.6} /> Gerar outra
            </button>
            <button
              onClick={copy}
              className="inline-flex items-center gap-2 border border-[#0A1128]/20 text-[#0A1128] hover:border-[#C5A059] hover:text-[#C5A059] px-4 py-2.5 text-xs tracking-[0.2em] uppercase transition-colors"
              data-testid="offer-copy"
            >
              <Copy size={13} strokeWidth={1.6} /> Copiar
            </button>
            <button
              onClick={openMailto}
              className="inline-flex items-center gap-2 bg-[#0A1128] text-[#F4F1EB] hover:bg-[#162243] px-4 py-2.5 text-xs tracking-[0.2em] uppercase transition-colors"
              data-testid="offer-mailto"
            >
              <Mail size={13} strokeWidth={1.6} /> Abrir no email
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ConversationDrawer = ({ open, onClose, sessionId, onAnnotated }) => {
  const [detail, setDetail] = useState(null);
  const [showOffer, setShowOffer] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !sessionId) return;
    setDetail(null);
    api
      .get(`/admin/concierge/sessions/${sessionId}`)
      .then(({ data }) => setDetail(data))
      .catch(() => toast.error("Falha ao carregar conversa"));
  }, [open, sessionId]);

  const toggleHandled = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/concierge/sessions/${sessionId}/annotate`, {
        handled: !detail.handled,
      });
      setDetail({ ...detail, handled: data.handled });
      onAnnotated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async (notes) => {
    setSaving(true);
    try {
      await api.post(`/admin/concierge/sessions/${sessionId}/annotate`, { notes });
      setDetail({ ...detail, notes });
      toast.success("Nota guardada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 bg-[#0A1128]/50 flex justify-end" data-testid="conversation-drawer">
      <div className="bg-[#FCFAF8] w-full max-w-xl h-full flex flex-col border-l border-[#0A1128]/15 shadow-2xl overflow-hidden">
        <div className="bg-[#0A1128] text-[#F4F1EB] px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="overline text-[#C5A059] text-[10px]">Conversa</div>
            <div className="font-serif-display text-lg font-light mt-1 break-all">
              {sessionId?.slice(0, 12)}…
            </div>
            {detail && (
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#F4F1EB]/60 mt-2">
                {detail.turns} turnos · {detail.lang.toUpperCase()}
                {detail.guest_email && <> · {detail.guest_email}</>}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#F4F1EB]/70 hover:text-[#F4F1EB] p-1"
            data-testid="conversation-close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!detail && <div className="text-sm text-[#3A435E]">…</div>}
          {detail?.logs?.map((lg, idx) => (
            <div key={lg.id || `l-${idx}`} className="space-y-2">
              <div className="flex justify-end">
                <div className="max-w-[85%] px-4 py-2.5 text-sm bg-[#0A1128] text-[#F4F1EB] rounded-2xl rounded-br-md whitespace-pre-wrap">
                  {lg.user_message}
                </div>
              </div>
              <div className="flex justify-start">
                <div className="max-w-[85%] px-4 py-2.5 text-sm bg-[#F4F1EB] text-[#0A1128] rounded-2xl rounded-bl-md border border-[#0A1128]/10 whitespace-pre-wrap">
                  {lg.reply}
                </div>
              </div>
            </div>
          ))}
          {detail && (
            <div className="pt-4 border-t border-[#0A1128]/10">
              <div className="overline text-[#C5A059] text-[10px] mb-2">Nota interna</div>
              <textarea
                defaultValue={detail.notes || ""}
                onBlur={(e) => e.target.value !== (detail.notes || "") && saveNotes(e.target.value)}
                placeholder="Escreva uma nota sobre esta lead (guardada ao sair do campo)"
                rows={3}
                className="w-full bg-transparent border border-[#0A1128]/15 focus:border-[#C5A059] outline-none p-3 text-sm resize-none"
                data-testid="conversation-notes"
              />
            </div>
          )}
        </div>
        {detail && (
          <div className="border-t border-[#0A1128]/10 p-4 flex flex-wrap gap-3 bg-[#FCFAF8]">
            <button
              onClick={toggleHandled}
              disabled={saving}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs tracking-[0.2em] uppercase border transition-colors ${
                detail.handled
                  ? "bg-[#C5A059]/15 border-[#C5A059] text-[#C5A059]"
                  : "border-[#0A1128]/20 text-[#0A1128] hover:border-[#C5A059]"
              }`}
              data-testid="conversation-toggle-handled"
            >
              <Check size={13} strokeWidth={1.8} />
              {detail.handled ? "Tratado" : "Marcar como tratado"}
            </button>
            <button
              onClick={() => setShowOffer(true)}
              className="inline-flex items-center gap-2 bg-[#0A1128] text-[#F4F1EB] hover:bg-[#162243] px-4 py-2.5 text-xs tracking-[0.2em] uppercase transition-colors ml-auto"
              data-testid="conversation-generate-offer"
            >
              <Sparkles size={13} strokeWidth={1.6} />
              Gerar oferta com IA
            </button>
          </div>
        )}
      </div>
      <OfferModal
        open={showOffer}
        onClose={() => setShowOffer(false)}
        sessionId={sessionId}
        guestEmail={detail?.guest_email}
      />
    </div>
  );
};

const ConversationsPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyMeeting, setOnlyMeeting] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [day, setDay] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (onlyMeeting) params.set("only_meeting", "true");
    if (onlyOpen) params.set("only_open", "true");
    if (day) params.set("day", day);
    api
      .get(`/admin/concierge/sessions?${params.toString()}`)
      .then(({ data }) => setSessions(data))
      .catch(() => toast.error("Falha ao carregar conversas"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [onlyMeeting, onlyOpen, day]);

  const totals = useMemo(
    () => ({
      total: sessions.length,
      leads: sessions.filter((s) => s.suggest_meeting).length,
      open: sessions.filter((s) => !s.handled).length,
    }),
    [sessions]
  );

  return (
    <div className="space-y-6" data-testid="admin-conversations">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Conversas", value: totals.total, testid: "conv-total" },
          { label: "Leads quentes", value: totals.leads, testid: "conv-leads" },
          { label: "Por tratar", value: totals.open, testid: "conv-open" },
        ].map((m) => (
          <div key={m.label} className="bg-[#FCFAF8] border border-[#0A1128]/10 p-5" data-testid={m.testid}>
            <div className="overline text-[10px] text-[#C5A059]">{m.label}</div>
            <div className="font-serif-display text-3xl font-light text-[#0A1128] mt-1">{m.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-[#FCFAF8] border border-[#0A1128]/10 p-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#3A435E]">
          <Filter size={13} strokeWidth={1.6} /> Filtros
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-[#0A1128] cursor-pointer">
          <input
            type="checkbox"
            checked={onlyMeeting}
            onChange={(e) => setOnlyMeeting(e.target.checked)}
            data-testid="filter-only-meeting"
          />
          Apenas com sugestão de reunião
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-[#0A1128] cursor-pointer">
          <input
            type="checkbox"
            checked={onlyOpen}
            onChange={(e) => setOnlyOpen(e.target.checked)}
            data-testid="filter-only-open"
          />
          Apenas por tratar
        </label>
        <input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="bg-transparent border border-[#0A1128]/15 px-3 py-1.5 text-xs text-[#0A1128]"
          data-testid="filter-day"
        />
        {day && (
          <button
            onClick={() => setDay("")}
            className="text-xs text-[#3A435E] hover:text-[#C5A059] underline"
            data-testid="filter-day-clear"
          >
            limpar
          </button>
        )}
        <button
          onClick={load}
          className="ml-auto inline-flex items-center gap-2 text-xs text-[#0A1128] hover:text-[#C5A059]"
          data-testid="conv-refresh"
        >
          <RefreshCw size={13} strokeWidth={1.6} /> Atualizar
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F1EB] text-[#3A435E]">
            <tr>
              <th className="text-left px-5 py-3 font-normal">Sessão</th>
              <th className="text-left px-5 py-3 font-normal">Primeira mensagem</th>
              <th className="text-left px-5 py-3 font-normal">Turnos</th>
              <th className="text-left px-5 py-3 font-normal">Email</th>
              <th className="text-left px-5 py-3 font-normal">Estado</th>
              <th className="text-left px-5 py-3 font-normal">Quando</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-6 text-center text-[#3A435E]">…</td></tr>
            ) : sessions.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-6 text-center text-[#3A435E]">Sem conversas com estes filtros.</td></tr>
            ) : (
              sessions.map((s) => (
                <tr
                  key={s.session_id}
                  onClick={() => setSelected(s.session_id)}
                  className="border-t border-[#0A1128]/10 hover:bg-[#F4F1EB] cursor-pointer"
                  data-testid={`conv-row-${s.session_id}`}
                >
                  <td className="px-5 py-3 font-mono text-xs text-[#3A435E]">{s.session_id.slice(0, 10)}…</td>
                  <td className="px-5 py-3 max-w-[300px] truncate" title={s.first_user_message}>
                    {s.first_user_message || "—"}
                  </td>
                  <td className="px-5 py-3">{s.turns}</td>
                  <td className="px-5 py-3 text-xs">{s.guest_email || "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {s.suggest_meeting && (
                        <span className="text-[10px] tracking-[0.15em] uppercase bg-[#C5A059]/15 text-[#C5A059] px-2 py-0.5 border border-[#C5A059]/40">
                          Lead
                        </span>
                      )}
                      {s.handled ? (
                        <span className="text-[10px] tracking-[0.15em] uppercase bg-[#0A1128]/10 text-[#3A435E] px-2 py-0.5">
                          Tratado
                        </span>
                      ) : (
                        <span className="text-[10px] tracking-[0.15em] uppercase text-[#3A435E]/70 px-2 py-0.5">
                          Por tratar
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-[#3A435E]">{relativeTime(s.last_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ConversationDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        sessionId={selected}
        onAnnotated={load}
      />
    </div>
  );
};

export default ConversationsPanel;
